import React, { forwardRef, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import _ from 'lodash'
import clsx from 'clsx'
import useControlled from '../../utils/useControlled'
import withTheme from '../styles/withTheme'
import Icon from '../Icon'

import './QuantitySelector.scss'


function countDecimals(number) {

    if (Math.floor(number.valueOf()) === number.valueOf()) return 0;

    var str = number.toString();
    if (str.indexOf(".") !== -1 && str.indexOf("-") !== -1) {
        return str.split("-")[1] || 0;
    } else if (str.indexOf(".") !== -1) {
        return str.split(".")[1].length || 0;
    }
    return str.split("-")[1] || 0;
}


const QuantitySelector = forwardRef(function QuantitySelector(props, ref) {
	const {
		className,
		debouncing = 500,
		defaultValue = 1,
		disabled,
		inputRef,
		max,
		min = 1,
		onChange,
		onError,
		onValueChange,
		step = 1,
		value: valueProp,
		...other
	} = props

	const [value, setValue] = useControlled({
		controlled: valueProp,
		default: typeof defaultValue === 'number' ? defaultValue.toString() : min.toString()
	})
	const [previousValue, setPreviousValue] = useState(value)

	const isControlled = valueProp !== undefined && valueProp !== null

	const emitChange = (emmitingValue) => {
		const newValue = checkIntroducedNumber(emmitingValue)
		setValue(newValue)
		previousValue !== newValue &&
			typeof onValueChange === 'function' &&
			newValue !== '' &&
			onValueChange(newValue)
	}

	const emitChangeDebounced = _.debounce(emitChange, debouncing)

	useEffect(() => {
		if (previousValue !== value) {
			emitChangeDebounced(value)
		}
		return emitChangeDebounced.cancel
	}, [value])

	const handleChange = (e, reason) => {
		if (isControlled) {
			onChange?.(e.target.value, reason)
		} else {
			setPreviousValue(value)
			setValue(e.target.value)
		}
	}

	const handleClickAdd = () => {
		const newValue = checkComputedNumber((parseFloat(value) + step).toFixed(countDecimals(step)))
		isControlled ? onChange?.(newValue, 'added') : setValue(newValue)
	}

	const handleClickSusbstract = () => {
		const newValue = checkComputedNumber((parseFloat(value) - step).toFixed(countDecimals(step)))
		setPreviousValue(value)
		isControlled ? onChange?.(newValue, 'substracted') : setValue(newValue)
	}

	const handleFocus = (e) => e.target.select()

	const handleBlur = (e) => {
		e.target.value === '' && setValue(previousValue)
	}

	const checkComputedNumber = (value) => {

		if (!isControlled && max && max < +value) {
			handleError('maxValue')
			return max.toString()
		}
		if (!isControlled && min > +value) {
			handleError('minValue')
			return min.toString()
		}
		return value.toString()
	}

	const checkIntroducedNumber = (value) => {

		if (value === '' || typeof value !== 'string') {
			return ''
		}

		let trimValue
		if (!countDecimals(step)) {
			// remove all zeros not preceded by latin letters, decimal digits, underscores, or +/- symbol
			// remove decimal digits
			trimValue = value.replace(/(\b0+|([.,']\d+))/g, '') === ''
				? '0'
				: value.replace(/(\b0+|([.,']\d+))/g, '')
		} else {
			trimValue = value.replace(/[,']\d+/g, '') === ''
				? '0'
				: value.replace(/[,']\d+/g, '')
		}

		if (!isControlled && max && max < +value) {
			handleError('maxValue')

			return parseInt(trimValue.slice(0, max.toString().length), 10) <= max
				? trimValue.slice(0, max.toString().length)
				: trimValue.slice(0, max.toString().length - 1)
		}
		if (!isControlled && min && min > +value) {
			handleError('minValue')
			if (min >= 0) {
				return min.toString()
			}
			return parseInt(trimValue.slice(0, min.toString().length), 10) >= min
				? trimValue.slice(0, min.toString().length)
				: trimValue.slice(0, min.toString().length - 1)
		}
		return trimValue
	}

	const handleError = (reason) => {
		reason && console.log(reason)
		typeof onError === 'function' && onError(reason)
	}

	return (
		<div ref={ref} className={clsx('Vb-qty-selector', className)} {...other}>
			<button className="Vb-qty-selector__btn-substract" onClick={handleClickSusbstract} disabled={disabled}>
				<Icon size={0.75}>minus</Icon>
			</button>
			<input
				ref={inputRef}
				type="number"
				value={value}
				onChange={(e) => handleChange(e, 'typed')}
				onFocus={handleFocus}
				onBlur={handleBlur}
				min={min}
				max={max}
				step={step}
				disabled={disabled}
			/>
			<button className="Vb-qty-selector__btn-add" onClick={handleClickAdd} disabled={disabled}>
				<Icon size={0.75}>plus</Icon>
			</button>
		</div>
	)
})

QuantitySelector.propTypes = {
	/**
	 * Default value of the input.
	 */
	defaultValue: PropTypes.number,

	/**
	 * Debouncing time in ms.
	 * @default 500
	 */
	debouncing: PropTypes.number,

	/**
	 * If `true`, the input and buttons are disabled.
	 * @default false
	 */
	disabled: PropTypes.bool,

	/**
	 * Max value allowed.
	 */
	max: PropTypes.number,

	/**
	 * Min value allowed.
	 */
	min: PropTypes.number,

	/**
	 * Callback fired when value changed and it's controlled.
	 *
	 * @param {number} value The input value.
	 * @param {'added' | 'substracted' | 'typed'} reason The reason why the value is been updated.
	 */
	onChange: PropTypes.func,
	
	/**
	 * Callback fired when value changed.
	 *
	 * @param {number} value The input value.
	 */
	onValueChange: PropTypes.func,

	/**
	 * Step value.
	 */
	step: PropTypes.number,

	/**
	 * Controlled value.
	 */
	value: PropTypes.number
}

export default withTheme('Vb-qty-selector')(QuantitySelector)